import { getMultiCartItems } from 'api/cart/getMultiCartItems'
import { getMultiCartSummaries } from 'api/cart/getMultiCartSummaries'
import { getMultiCartMembers } from 'api/cart/getMultiCartMembers'
import { assertEvent, assign, DoneActorEvent, ErrorActorEvent, fromPromise, log, setup, spawnChild, sendTo, or, not } from 'xstate'
import { inviteUsersToMultiCart, MultiCartInvitationUsers } from 'api/cart/inviteUsersToMultiCart'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { createMultiCart } from 'api/cart/createMultiCart'
import { addItemsToMultiCart } from 'api/cart/addItemsToMultiCart'
import { History } from 'history'
import { joinUserToMultiCart } from 'api/cart/joinUserToMultiCart'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { leaveMultiCart } from 'api/cart/leaveMultiCart'
import { revokeUserAccessMultiCart } from 'api/cart/revokeUserAccessMultiCart'
import { get as getLocalStorage, set as setLocalStorage } from 'lib/storage/isomorphicLocalStorage'
import { moveItemsToTargetCart } from 'api/cart/moveItemsToTargetCart'
import { createAndMoveItemsToTargetCart } from 'api/cart/createAndMoveItemsToTargetCart'
import { deleteItemsFromMultiCart } from 'api/cart/deleteItemsFromMultiCart'
import { purchaseMultiCartItems } from 'api/cart/purchaseMultiCartItems'
import { multiCartCreateCartEvent, multiCartDeleteItemEvent, multiCartSaveAllForLaterEvent, multiCartSaveMultipleItemsForLaterEvent } from 'analytics/snowplow/events'
import { multiCartDeleteItemMachine } from 'components/Pages/UserCartPage/SharedCarts/multiCartDeleteItemMachine'

const ACTIVE_CART_ID_STORAGE_KEY = 'sharedCarts.activeCartId'

export type MultiCartMachineInput = {
  memberId?: string
  history: History<unknown>
  currentRegionCode: string
  sharedCartsFeatureFlag: boolean
}

export type CartModalConfig = {
  multiCartItemIdsToMove: Array<string>
}

export const CART_MODAL_EVENT_NAME = 'multiCartMachineModal'

type MultiCartMachineContext = {
  cartSummary: { status: 'loaded', data: MachineContext.CartSummary } | { status: 'error', error: string } | { status: 'loading' }
  cartItems: { status: 'loaded', data: MachineContext.CartItems } | { status: 'error', error: string } | { status: 'loading' }
  cartMembers: { status: 'loaded', data: MachineContext.CartMembers } | { status: 'error', error: string } | { status: 'loading' }
  activeCartId?: string
  // Redux state from input
  memberId?: string
  history: History<unknown>
  currentRegionCode: string
  sharedCartsFeatureFlag: boolean
  creatingNewCart: boolean
}

type InviteMultiCartUsers = {
  type: 'invite multi cart users'
  cartId: string
  users: MultiCartInvitationUsers
}

type MoveItemsToMultiCart = {
  type: 'select and move items to target cart'
  multiCartItemIdsToMove: Array<string>
  targetCartId: string
}

type RemoveActiveCart = {
  type: 'remove active cart id'
}

type UpdateMultiCart = {
  type: 'update multi cart'
  cartId: string
  name: string
}

type LeaveMultiCart = {
  type: 'leave multi cart'
  cartId: string
}

type RevokeUserAccess = {
  type: 'revoke user access'
  cartId: string
  userId: string
}

type SetActiveCart = {
  type: 'set active cart'
  cartId: string
}

type CreateAndMoveItemsToTargetCart = {
  type: 'create and move items to target cart'
  cartName: string
  multiCartItemIdsToMove: Array<string>
  skipNotification: boolean
}

type UpdateCartItem = {
  type: 'update cart item'
  cartId: string
  multiCartItem: App.MultiCartItem
}

type RemoveItemFromCart = {
  type: 'remove item from cart'
  cartId: string
  itemIds: Array<string>
}

type MarkItemAsDeleted = {
  type: 'mark item as deleted'
  cartId: string
  itemIds: Array<string>
}

type RestoreItemStates = {
  type: 'restore item states'
  cartId: string
  itemIds: Array<string>
  historyStates: Record<string, App.MultiCartItemState>
}

type PurchaseActiveCartItems = {
  type: 'purchase active cart items'
  checkoutCartId: string
  orderId: string
}

type DeleteItemsFromGivenCart = {
  type: 'delete items from given cart'
  cartId: string
  itemIds: Array<string>
  currentStates: Record<string, App.MultiCartItemState>
}

type HardDeleteItemsFromGivenCart = {
  type: 'hard delete items from given cart'
  cartId: string
  itemIds: Array<string>
  currentStates: Record<string, App.MultiCartItemState>
}

type UndoDeleteItem = {
  type: 'undo delete'
  itemIds: Array<string>
}

type MultiCartMachineEvents = {
  type: 'successful login'
  memberId: string
} | {
  type: 'failed login'
} | {
  type: 'join multi cart'
  sharedCartId: string
} | {
  type: 'add items to active cart'
  items: Array<App.Checkout.AnyItem>
  requestConnectedRooms?: boolean
  skipNotification: boolean
} | {
  type: 'empty cart items of active cart'
} |
{
  type: 'update cart items save for later'
  itemIds: Array<string>,
  saveForLater: boolean
} |
InviteMultiCartUsers | MoveItemsToMultiCart | RemoveActiveCart | CreateAndMoveItemsToTargetCart
| UpdateMultiCart | LeaveMultiCart | RevokeUserAccess | SetActiveCart | UpdateCartItem | RemoveItemFromCart | MarkItemAsDeleted | RestoreItemStates | PurchaseActiveCartItems
| DeleteItemsFromGivenCart | HardDeleteItemsFromGivenCart | UndoDeleteItem
| DoneActorEvent<{ createdCart: App.MultiCartSummary, targetCartId: string }, string>
| DoneActorEvent<{ cartId: string, user: App.MultiCartMember }, string>
| DoneActorEvent<MachineContext.CartSummary, string>
| DoneActorEvent<{ cartItems: MachineContext.CartItems }, string>
| DoneActorEvent<Array<MachineContext.CartItems>, string>
| DoneActorEvent<Array<MachineContext.CartMembers>, string>
| ErrorActorEvent<{ status: number, message: string }, string>

/**
 * State machine docs - https://stately.ai/docs/state-machines-and-statecharts
 */

const multiCartMachine = setup({
  types: {
    context: {} as MultiCartMachineContext,
    events: {} as MultiCartMachineEvents,
    input: {} as MultiCartMachineInput,
  },
  actors: {
    createMultiCart: fromPromise(({ input }: { input: { cartName: string, items: Array<App.Checkout.AnyItem> } }) => createMultiCart(input.cartName, input.items)),
    addItemsToMultiCart: fromPromise(({ input }: { input: { cartId: string, items: Array<App.Checkout.AnyItem>, requestConnectedRooms?: boolean, skipNotification: boolean } }) => addItemsToMultiCart(input.cartId, input.items, input.requestConnectedRooms ?? false, input.skipNotification)),
    leaveMultiCart: fromPromise(({ input }: { input: { cartId: string } }) => leaveMultiCart(input.cartId)),
    getMultiCarts: fromPromise(({ input }: { input: { isSharedCartsFeatureEnabled: boolean, isSharedCartInvitePage: boolean } }) => getMultiCartSummaries(input)),
    getMultiCartItems: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartItems))),
    getMultiCartMembers: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartMembers))),
    inviteUsersToMultiCart: fromPromise(({ input }: { input: { cartId: string, users: MultiCartInvitationUsers } }) => inviteUsersToMultiCart(input.cartId, input.users)),
    joinUserToMultiCart: fromPromise(({ input }: { input: { sharedCartId: string } }) => joinUserToMultiCart(input.sharedCartId)),
    revokeUserAccessMultiCart: fromPromise(({ input }: { input: { cartId: string, userId: string } }) => revokeUserAccessMultiCart(input.cartId, input.userId)),
    moveItemsToTargetCart: fromPromise(({ input }: { input: { sourceCartId: string, targetCartId: string, itemIdsToMove: Array<string> } }) => moveItemsToTargetCart(input)),
    createAndMoveItemsToTargetCart: fromPromise(({ input }: { input: { sourceCartId: string, cartName: string, itemIdsToMove: Array<string>, skipNotification: boolean } }) => createAndMoveItemsToTargetCart(input)),
    deleteItemsInMultiCart: fromPromise(({ input }: { input: { cartId: string, itemIdsToDelete: Array<string> } }) => deleteItemsFromMultiCart(input.cartId, input.itemIdsToDelete)),
    purchaseMultiCartItems: fromPromise(({ input }: { input: { multiCartId: string, checkoutCartId: string, orderId: string } }) => purchaseMultiCartItems(input.multiCartId, input.checkoutCartId, input.orderId)),
  },
  actions: {
    assignLoggedInMemberId: assign({
      memberId: ({ event }: { event: { type: 'successful login', memberId: string } }) => {
        return event.memberId
      }
    }),
    assignCartSummary: assign({
      cartSummary: ({ event }: { event: DoneActorEvent<MachineContext.CartSummary, string> }) => ({
        status: 'loaded' as const,
        data: event.output,
      })
    }),
    assignCartSummaryFetchError: assign({
      cartSummary: () => ({
        status: 'error' as const,
        error: 'Failed to load cart summary'
      })
    }),
    assignCartItems: assign({
      cartItems: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string> }) => {
        // Merge all cart items from Promise.all results into a single object
        return {
          status: 'loaded' as const,
          data: event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems)
        }
      }
    }),
    emptyCartItemsOfActiveCart: assign({
      cartItems: ({ context }: { context: MultiCartMachineContext }) => {
        const { activeCartId } = context
        if (!activeCartId) {
          return context.cartItems
        }

        return {
          ...context.cartItems,
          data: {
            ...(context.cartItems.status === 'loaded' ? context.cartItems.data : {}),
            [activeCartId]: {}
          }
        }
      }
    }),
    appendCartItems: assign({
      cartItems: ({ event, context }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string>, context: MultiCartMachineContext }) => {
        const newCartItems = event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems)

        return {
          status: 'loaded' as const,
          data: {
            ...context.cartItems.status === 'loaded' ? context.cartItems.data : {},
            ...newCartItems,
          }
        }
      }
    }),
    appendItemsToCart: assign({
      cartItems: ({ event, context }: { event: DoneActorEvent<{ cartItems: MachineContext.CartItems }, string>, context: MultiCartMachineContext }) => {
        const newCartItems = event.output.cartItems
        const existingData = context.cartItems.status === 'loaded' ? context.cartItems.data : {}
        const updatedData = { ...existingData }

        Object.keys(newCartItems).forEach(cartId => {
          updatedData[cartId] = {
            ...existingData[cartId] || {},
            ...newCartItems[cartId],
          }
        })

        return {
          status: 'loaded' as const,
          data: updatedData
        }
      }
    }),
    appendCartMembers: assign({
      cartMembers: ({ event, context }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string>, context: MultiCartMachineContext }) => {
        const newCartMembers = event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers)
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartMembers.status === 'loaded' ? context.cartMembers.data : {},
            ...newCartMembers,
          }
        }
      }
    }),
    assignCartItemsFetchError: assign({
      cartItems: () => ({
        status: 'error' as const,
        error: 'Failed to load cart items'
      })
    }),
    assignCartMembers: assign({
      cartMembers: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string> }) => {
        // Merge all cart members from Promise.all results into a single object
        return {
          status: 'loaded' as const,
          data: event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers)
        }
      }
    }),
    assignCartMembersFetchError: assign({
      cartMembers: () => ({
        status: 'error' as const,
        error: 'Failed to load cart members'
      })
    }),
    assignNewlyCreatedCartSummary: assign({
      cartSummary: ({ event, context }: { event: DoneActorEvent<{ createdCart: App.MultiCartSummary, targetCartId: string }, string>, context: MultiCartMachineContext }) => {
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartSummary.status === 'loaded' ? context.cartSummary.data : {},
            [event.output.targetCartId]: event.output.createdCart,
          }
        }
      }
    }),
    loadActiveCartId: assign({
      activeCartId: ({ context }: { context: MultiCartMachineContext }) => {
        const activeCartIdFromStorage = getLocalStorage(ACTIVE_CART_ID_STORAGE_KEY)

        if (
          context.cartSummary.status === 'loaded' &&
          context.cartSummary.data &&
          activeCartIdFromStorage &&
          context.cartSummary.data[activeCartIdFromStorage]
        ) {
          return activeCartIdFromStorage
        }

        // active card id in storage is not available in the cart summary, set the default cart as active
        if (context.cartSummary.status === 'loaded' && context.cartSummary.data) {
          const data = context.cartSummary.data
          const defaultCart = Object.values(data).find(cart => cart.type === 'default')

          if (defaultCart) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, defaultCart.cartId)
            return defaultCart.cartId
          }
          const cartSummaryValues = Object.values(data)
          if (cartSummaryValues.length > 0 && cartSummaryValues[0]) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, cartSummaryValues[0].cartId)
            return cartSummaryValues[0].cartId
          }
        }

        return context.activeCartId
      },
    }),
    assignActiveCartId: assign({
      activeCartId: ({ context }: { context: MultiCartMachineContext }, params: { cartId: string }) => {
        if (context.activeCartId !== params.cartId) {
          setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, params.cartId)
        }

        return params.cartId
      },
    }),
    removeActiveCart: assign(
      ({ context }: { context: MultiCartMachineContext }) => {
        if (context.cartSummary.status === 'loaded' && context.cartItems.status === 'loaded' && context.cartMembers.status === 'loaded') {
          if (!context.activeCartId) {
            return context
          }

          const defaultCart = Object.values(context.cartSummary.data).find(cart => cart.type === 'default')
          let newActiveCartId = context.activeCartId

          if (defaultCart && defaultCart.cartId !== context.activeCartId) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, defaultCart.cartId)

            newActiveCartId = defaultCart.cartId
          }
          // remove object of active cart from cart summary, cart items and cart members
          const { [context.activeCartId]: _removedCartSummary, ...restCartSummary } = context.cartSummary.data
          const { [context.activeCartId]: _removedCartItems, ...restCartItems } = context.cartItems.data
          const { [context.activeCartId]: _removedCartMembers, ...restCartMembers } = context.cartMembers.data

          return {
            activeCartId: newActiveCartId,
            cartSummary: {
              ...context.cartSummary,
              data: restCartSummary
            },
            cartItems: {
              ...context.cartItems,
              data: restCartItems
            },
            cartMembers: {
              ...context.cartMembers,
              data: restCartMembers
            }
          }
        }

        return context
      },
    ),
    assignRevokedUserAccess: assign({
      cartMembers: ({ event, context }: { event: DoneActorEvent<{ cartId: string, user: App.MultiCartMember }, string>, context: MultiCartMachineContext }) => {
        if (context.cartMembers.status !== 'loaded' || !event.output.user.userId) {
          return context.cartMembers
        }
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartMembers.data,
            [event.output.cartId]: context.cartMembers.data[event.output.cartId].map(member =>
              member.userId === event.output.user.userId
                ? event.output.user
                : member
            )
          }
        }
      }
    }),
    assignUpdatedCartSummary: assign({
      cartSummary: ({ event, context }: { event: UpdateMultiCart, context: MultiCartMachineContext }) => {
        if (context.cartSummary.status !== 'loaded') {
          return context.cartSummary
        }

        return {
          ...context.cartSummary,
          data: {
            ...context.cartSummary.data,
            [event.cartId]: {
              ...context.cartSummary.data[event.cartId],
              name: event.name,
            }
          }
        }
      }
    }),
    assignCreatingNewCart: assign({
      creatingNewCart: true
    }),
    unassignCreatingNewCart: assign({
      creatingNewCart: false
    }),
    updateCartItem: assign({
      cartItems: ({ event, context }: { event: UpdateCartItem, context: MultiCartMachineContext }) => {
        if (context.cartItems.status !== 'loaded') {
          return context.cartItems
        }

        const updatedCartItems = { ...context.cartItems.data }

        // Update the specific item in the cart
        if (updatedCartItems[event.cartId]) {
          updatedCartItems[event.cartId] = {
            ...updatedCartItems[event.cartId],
            [event.multiCartItem.id]: event.multiCartItem
          }
        }

        return {
          status: 'loaded' as const,
          data: updatedCartItems
        }
      }
    }),
    markItemAsDeleted: assign({
      cartItems: ({ event, context }: { event: MarkItemAsDeleted, context: MultiCartMachineContext }) => {
        if (context.cartItems.status !== 'loaded') {
          return context.cartItems
        }

        const updatedCartItems = { ...context.cartItems.data }

        // Mark the specific items as deleted
        if (updatedCartItems[event.cartId]) {
          const cartItems = { ...updatedCartItems[event.cartId] }
          event.itemIds.forEach(itemId => {
            if (cartItems[itemId]) {
              cartItems[itemId] = {
                ...cartItems[itemId],
                state: 'deleted'
              }
            }
          })
          updatedCartItems[event.cartId] = cartItems
        }

        return {
          status: 'loaded' as const,
          data: updatedCartItems
        }
      }
    }),
    restoreItemStates: assign({
      cartItems: ({ event, context }: { event: RestoreItemStates, context: MultiCartMachineContext }) => {
        if (context.cartItems.status !== 'loaded') {
          return context.cartItems
        }

        const updatedCartItems = { ...context.cartItems.data }
        // Mark the specific items as active
        if (updatedCartItems[event.cartId]) {
          const cartItems = { ...updatedCartItems[event.cartId] }
          event.itemIds.forEach(itemId => {
            if (cartItems[itemId]) {
              cartItems[itemId] = {
                ...cartItems[itemId],
                state: event.historyStates[itemId] ?? 'active'
              }
            }
          })
          updatedCartItems[event.cartId] = cartItems
        }

        return {
          status: 'loaded' as const,
          data: updatedCartItems
        }
      }
    }),
    removeItemFromCart: assign({
      cartItems: ({ context }: { context: MultiCartMachineContext }, params: { cartId: string, itemIds: Array<string> }) => {
        if (context.cartItems.status !== 'loaded') {
          return context.cartItems
        }

        const updatedCartItems = { ...context.cartItems.data }

        // Remove the specific items from cart
        if (updatedCartItems[params.cartId]) {
          const cartItems = { ...updatedCartItems[params.cartId] }
          params.itemIds.forEach(itemId => {
            delete cartItems[itemId]
          })
          updatedCartItems[params.cartId] = cartItems
        }

        return {
          status: 'loaded' as const,
          data: updatedCartItems
        }
      }
    }),
    updateCartItemsSaveForLater: assign({
      cartItems: ({ event, context }) => {
        assertEvent(event, 'update cart items save for later')

        if (context.cartItems.status !== 'loaded' || !context.activeCartId) {
          return context.cartItems
        }

        const updatedCartItems = { ...context.cartItems.data }

        // Remove the specific items from cart
        if (updatedCartItems[context.activeCartId]) {
          const cartItems = { ...updatedCartItems[context.activeCartId] }

          event.itemIds.forEach(itemId => {
            cartItems[itemId] = {
              ...cartItems[itemId],
              state: event.saveForLater ? 'save_for_later' : 'active'
            }
          })
          updatedCartItems[context.activeCartId] = cartItems
        }

        return {
          status: 'loaded' as const,
          data: updatedCartItems
        }
      }
    }),
    spawnDeleteItemMachine: spawnChild(multiCartDeleteItemMachine, {
      id: ({ event }: { event: MultiCartMachineEvents }) => {
        assertEvent(event, 'delete items from given cart')
        return `deleteItemMachine-${event.itemIds.join('-')}`
      },
      input: ({ event }: { event: MultiCartMachineEvents }) => {
        assertEvent(event, ['delete items from given cart', 'hard delete items from given cart'])

        return {
          cartId: event.cartId,
          itemIds: event.itemIds,
          currentStates: event.currentStates || {},
        }
      },
      // this as any is temporary fix to fix the type error
    } as any),

    sendSoftDeleteEvent: sendTo(({ event }) => {
      assertEvent(event, 'delete items from given cart')
      return `deleteItemMachine-${event.itemIds.join('-')}`
    }, { type: 'soft delete' }),

    sendHardDeleteEvent: sendTo(({ event }) => {
      assertEvent(event, 'hard delete items from given cart')
      return `deleteItemMachine-${event.itemIds.join('-')}`
    }, { type: 'hard delete' }),

    sendUndoDeleteItemEvent: sendTo(({ event }) => {
      assertEvent(event, 'undo delete')
      return `deleteItemMachine-${event.itemIds.join('-')}`
    }, { type: 'undo delete' }),
    sendSaveLaterTrackingEvent: ({ event, context }) => {
      assertEvent(event, 'update cart items save for later')

      if (context.cartItems.status !== 'loaded' || !context.activeCartId) {
        return
      }

      const allCartItems = context.cartItems.status === 'loaded' && context.cartItems.data[context.activeCartId] ? context.cartItems.data[context.activeCartId] : {}
      const allItemValues = Object.values(allCartItems).filter(item => item.state === 'active')

      if (allItemValues.length === event.itemIds.length && event.saveForLater) {
        multiCartSaveAllForLaterEvent(event.itemIds.map(itemId => allCartItems[itemId].item))
        return
      }

      multiCartSaveMultipleItemsForLaterEvent(event.itemIds.map(itemId => allCartItems[itemId].item), event.saveForLater)
    },
    trackMultiCartDeleteItemEvent: ({ event, context }) => {
      const removeEvent = event as RemoveItemFromCart
      const { cartId, itemIds } = removeEvent

      // Get items from machine context, not Redux
      if (context.cartItems.status === 'loaded' && context.cartItems.data[cartId]) {
        const cartItems = context.cartItems.data[cartId]

        // Get the actual items being deleted
        const itemsToDelete = itemIds
          .map(itemId => cartItems[itemId])
          .filter(Boolean)

        // Track analytics for each item individually using existing function
        // Note: This is fire-and-forget, but the analytics function is designed to be robust
        itemsToDelete.forEach(item => {
          multiCartDeleteItemEvent(item.item)
        })
      }
    },
    multiCartCreateCartEvent,
    // snackbar actions
    showInvitationSuccessSnackbar: () => {
      showSnackbar('Invitations sent!', 'success')
    },
    showInvitationErrorSnackbar: () => {
      showSnackbar('Failed to send invitations', 'critical')
    },
    showJoinCartSuccessSnackbar: () => {
      showSnackbar('You\'ve successfully joined the cart', 'success')
    },
    showJoinCartErrorSnackbar: ({ event }) => {
      assertEvent(event, 'xstate.error.actor.0.multiCartMachine.joiningUserToMultiCart')

      if (event.error?.status === 404) {
        showSnackbar('Failed to join the cart, cart not found', 'critical')
      } else {
        showSnackbar('Failed to join the cart', 'critical')
      }
    },
    showLeaveCartSuccessSnackbar: () => {
      showSnackbar('You have left the cart!', 'success')
    },
    showLeaveCartErrorSnackbar: () => {
      showSnackbar('Failed to leave cart', 'critical')
    },
    showRevokeUserAccessSuccessSnackbar: () => {
      showSnackbar('User access has been revoked!', 'success')
    },
    showRevokeUserAccessErrorSnackbar: () => {
      showSnackbar('Failed to revoke user access', 'critical')
    },
    showAddItemsToCartSuccessSnackbar: ({ context, event }) => {
      const newCartItems = (event as DoneActorEvent<{ cartItems: MachineContext.CartItems }, string>).output.cartItems
      const { activeCartId } = context

      if (!activeCartId) {
        return
      }

      const cartName = context.cartSummary.status === 'loaded' && context.cartSummary.data[context.activeCartId!] ? context.cartSummary.data[context.activeCartId!].name : 'cart'

      const items = Object.values(newCartItems[activeCartId])
      const itemIds = items.map(i => i.id)

      showSnackbar(`Item added to ${cartName === 'default' ? 'cart' : cartName}`, 'success', {
        action: {
          label: 'Change cart',
          onAction: () => {
            document.dispatchEvent(new CustomEvent<CartModalConfig>(CART_MODAL_EVENT_NAME, {
              detail: { multiCartItemIdsToMove: itemIds }
            }))
          }
        }
      }) // TODO: CRO-5817 add item name and cart name
    },
    showDeleteAllItemsFromCartSuccessSnackbar: ({ context }) => {
      const cartName = context.cartSummary.status === 'loaded' && context.cartSummary.data[context.activeCartId!] ? context.cartSummary.data[context.activeCartId!].name : 'cart'
      showSnackbar(`All items have been deleted from ${cartName === 'default' ? 'cart' : cartName}`, 'success')
    },
    showDeleteAllItemsFromCartErrorSnackbar: () => {
      showSnackbar('Failed to delete all items from cart', 'critical')
    },
    showMoveItemsToCartSuccessSnackbar: ({ context }) => {
      const cartName = context.cartSummary.status === 'loaded' && context.cartSummary.data[context.activeCartId!] ? context.cartSummary.data[context.activeCartId!].name : 'cart'
      showSnackbar(`Item added to ${cartName === 'default' ? 'cart' : cartName}`, 'success', { action: { label: 'View cart', to: '/cart' } })
    },
    showAddItemsToCartErrorSnackbar: () => {
      showSnackbar('Failed to add items to cart', 'critical')
    },
    showCreateCartSuccessSnackbar: ({ event }) => {
      showSnackbar(`${(event as DoneActorEvent<{ createdCart: App.MultiCartSummary, targetCartId: string }, string>).output.createdCart.name} cart has been created!`, 'success', { action: { label: 'View cart', to: '/cart' } })
    },
    showCreateCartErrorSnackbar: () => {
      showSnackbar('Failed to create cart', 'critical')
    },
    pushToCartPage: ({ context }: { context: MultiCartMachineContext }) => {
      context.history.push(buildUrl('/cart', context.currentRegionCode))
    },
    logErrorEvent: log(({ event }: {
      event: ErrorActorEvent<{ status: number, message: string }, string>
    }) => `assignCartSummaryFetchError ${JSON.stringify(event.error)}`),
  },
  guards: {
    isUserLoggedIn: ({ context }: { context: MultiCartMachineContext }) => !!context.memberId,
    isSharedCartsFeatureEnabled: ({ context }: { context: MultiCartMachineContext }) => context.sharedCartsFeatureFlag,
    hasSharedCart: ({ event }) => {
      // Check if event is a done event from getMultiCarts actor with valid output
      if (!event.type.startsWith('xstate.done.actor')) {
        return false
      }
      // the ID of the invocation
      assertEvent(event, 'xstate.done.actor.0.multiCartMachine.initializing.loadingCartSummary')

      // Type guard: check if event has output property
      const doneEvent = event as DoneActorEvent<MachineContext.CartSummary, string>
      return doneEvent.output && Object.keys(doneEvent.output).length > 0
    },
    isSharedCartInvitePage: ({ context }: { context: MultiCartMachineContext }) => context.history.location.pathname.includes('/cart/s/')
  },
}
).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCuAbALgSwMIEMAnTAWXwGMALbAOzADoqxyBrWqAZU30zAGIA2gAYAuolAAHAPaxsOKTXEgAHogCsANgDM9LQE4h27QBYhQ-QBoQAT0QAmO3ru6TARi0bjegBx7XAX38rNCw8IlIKajpGSmY2Gk5uXkFXMSQQaVl5RXTVBDU1HX1DDy1Tcz0rWwR3LSF6DVc9LQB2b3dCu21A4IwcAmIyKloGJlZ2Lh5+ATs0yRk5bAUlPM0igyMys0sbRFdvDXqtOxa1b2NXNRPzrR6QEP7woaiGWkX8dGwAL3Y+CAUGLAkgwHmFBpERvQ3jgPt92MI5hkFtkVvYhK56nZ2lomjizEInFU9q53PQWhorq0hJo9Go9MY7qCBhFhtFodhYT8EvR0FJ8BB2MyOKhkMgiNY-gCoTQAG5SFggvpglkvaXvT5cqA8vkChJCkViwjWBC0OXkHhLGgIhFKTKLZa5YkFMnmY7eOneITeb1EhAaU70DF+b1tYx2dF6RlK5nPSHsznsbX8wXhYWi8V8MCEQhSQj0CToHgAM1zyHoTKeELZNHVcO5vOTetTBvFJtlUnN2WtoltyMtqJqlzULq0bo9Xp9u3yzpaQkcehark8njKUdCMarrxrMI1iYbuqgzIAImBuNh0LBJdEgVNy9HK6yt7XNUmD8fT-hz7Abek7SjHTULRdIGmjhtSFJaN4dhqL67j0vQYauF4UHeEB-p2Gujzgo+ao7nWWr7imxAnmeF6vkRmAAJK8MgsDkQkV5bnKCp3uuD6qvGu71jqFEkZ+ZGEU2xDUWAtH0VAbZmhaCjdoif79gBrjQS09DNKc+xztoTQaLBdhLvQaiXMYEH0mUniYcqsbVs+e48UJmB8V+4nMiJYmCVAmbZrm+aFpgJaEGWFbYRx24clxBF2Ye4SOQJkUuTRdHuZJHbSVaog-vMWQKaAeRKWoKlqYZnpdDieg6VO7gEghGgHI0FLhqhtxBPc97BXGoUJtxjZRcRH5Oe5zIAKqwFmiWRYx0rMYqbFtdZeEvgN0V9bF3VDSNhBjd1yWdpasm9llDo5XsWKuPQvglDii5Qe4sGGadThIahrgtM0ZUWRuOGcfhzlLaRm1vuEw2jeJnk5nmBbFqWrFYSq7U2V1AO9X9P3EEDG3idtqV7b+faHSox3tGdGyjs9+x6VosGHMYZ0tMZ3hlK07g1e97GQoQYD8hKsCoOQ5BwLARYYAABLyUC0BlSIHTkR3TusJQmNslRTnYFz1I0o4GFiWhqGGGHNUFsPROznN8EW-GQCLUhi2lcm49L+MIHULRFP6SEaP6o7hhTU76GVujazr7RmEukb661hsMMbEASqachgELoJC+axBC6g63fj2ONSwOhynedK41XStPe9UkHUvQ5i004hy0gcLOzZHHPR3w5DG7wCdKkn4QS-JeO5YZzh6ahfgLu6ektL6jhqPUzv5Y4xgHIUoe9DNEf0FHEqtxz7f4DQEAJ1IMrx3HtFC5gUhn0QMCYF3xA93bA6QXoCGtIZbQbMZ2iT7TGiqWV4YUhDNseua8N58H5PvE+sAz4XwoDgI+t9MD32zopJCzgrqnA0HpUw+wlbVDsDiam7tNKtEaMYF6ICrKNxNiNdAzAb6733sgQ+x8EowMvoQa+iDkH2ntnkY4w5Zw4kKBpb0phjC+nEb-QotMVbUjpmoShm515NwlOzZhCC4HYAQcnG+2AIA8P-DLOoHhVI3DDH4UwUFfTFCOFBRC+Ug4aCUThMBqAJAQCmB3UI3DM6ZV4Y-Oc9R6Z0kuGVF6QgJE+09NTCxHgNhCDaJoFxqowF0PwAgxOujDHZQdiSemugsR6QIeBb03gbHkwMvSAwiS9D0iuCktmqi+DsymqndaQsKB81gBnW2KCZYaD9qcMwi59jnFpjBZWgzf5eCLn4FWWCFyNKNs0kaDDyDwPjtkvxksAkATqF4Qpnoy5LiuM0Gxs5vAIXJDiCkxgzLkOWdQ5uAArKQtBvE4F8X0vZAzyENAJB4FobRqSL0ngQ65dzR5hJCU8lRJt3GePbrooWJ8cl90QHUQogYF6mFHFg8MBQbGNGcJSJoZhsHohaHCsB6iWGopokLIsOZkDfP2r8h2dRLiBnRFcXwhQyg+BsV4eoJJBnhiUscxRYdV5UPhc3Q0LAGWiU6dAiAYA6G8AMTs3ufDMWV3oCrOpnh7nkjOJM0u+hqZLlaM0Q4Rc9YrxhnK2lcBz7s2Vaym8vBensqMZyyJ1Mq4LjuuYbBFSfCGoxO7S5xRl4tVlcosBokJCYGsIgz10CpBFk6Rs7RWzu46ofgBRwNURwYigguaCeDMXPWHDE-Qi4DA60dQm51SbmmIq8SiqBQtYAZPjv5EWUxCDor1Qgecz8biLgarnLoNjqTrHaCcRwi5TjSqdZZZRW8LQJBIOHCapp5TTXbThHdOA93h0xl2dKRb+kO1pCpRxelzC4IcJPA4Kkf7hmaEzPScLz3sH3avUG3kIZ+ShgbOVgHL2r2vbtW9Pz-X9yUlG4eTQknjw-flMkUEBUOGHu6ADbd2AAEE94kEPuwAAKlIYDMND3thYlB7dJGEjkYgJRmUNG6NXqPTtGSiG-W5P4b4F+6l36GE-iXWt2Kn6k3MAULoG621brPWxqAHGuM8fo8qUD4NfL+UCuHaDGmtNUYSLR3TzJ4OCZEGOx+YnTVv3ztJ30hMvB1O9BSaedTDJwogewVysBaOkbzUfZkjGprQzU6qQLCRguhfC2AGz-GsZCazhyvIKFAyoWnmTYwBQhUVVHmdE4ZwgV0kMK2ljOF4tQES1IMLmzIv-GrNF2rcWIAHka81-NqX2wCZtgIVIwmMUIHplc-YwyCtFfKcrRJp0QWzjpFiAwxgGQytPV1nrCUkstfCPpnykMAoxY+jtoLe2mvJYG1JG99m71ZeJGGMk03QJIXRL4SeTRTr7EGYuAoxxqTUq27FyE6TuOwYY21pix6zus2iBDoDfHBvpYe0hkTewB5oZehhsez0F3okNdrYhv7tDeDhUjqHemsxg2OxB07nXwcc0h1Aaz4RbPDce8h4kBS3TFKxTVScpclJqwuGPAoJJwk0rAMxdgaMwvdMvDDyacOmcrLlwkBXvN+ac+xv4nnfohnT0W2M8hhXJ7GV+7UR9o5IKbc3edppmuoDa6V0d8DRn4cNxUS7t3uu0v3YcwBaZZITejLERMyeZx6iGQXNrDwKssQqfV0xRYDEVdHuYyZ5RscL0SUDwh9HY3x25yJlJjw7oFz3OJRcM6ZQujhmLvSGrOfPqynTx5WnYHDOQbbyFbj+e9cZYN5jhAj6DJtBfTiXw76famF-otoppRmjJNB076IRZTysk0zd8IwWotq-75CLfmAd99Yi-vhKw-i+ZcNxP59GIZ9ODsDYwOZ1sSNBCVgwocLT-n732EgSg9170Z2P0323yiF3wOyANElgBv2DxlgfynyfzfVfx9hxB0HyUgmKUMndj-0gPYAADkwAAB3GMUSAAI1GkP2z0TRwn-ygJIPIKeCoNGgQO5zHyQg21Uhqhb3-jaHKlLnf0iTaBiScE9GcXXwRwYEYOILIIoOQGoI2hAJO2M3oNVDkISGYMUOUPgMLzs0QLyQ22pjKl8AsSwUENr1OmDkSX9Gnh1mdjhTeTeC13WiswPUzyYxPTB2iBcJrDcKzA8LgwMK5wx3G08BUhriBRBTOApHc0aBdE9E+y6DpiakdxkPoH8Pl3cN4xA27wMzUO9zXmyMCMIGCJhg4PCNL3+WiP9FiLBXn2pgcApCHEiSQmBSkIyJ9w0UuzgNo2oyvlPFaylCzx8I3xBAswayu0GM4WGI51CP112UNwEXLREU9DEUiQjWfhVndEMBIV8DhV6ISxmKGMwEiwKPpy91T3LCmMa1mOvluxSiD04PG1WKEUuBnwnHERsXWwMkOAOEcBOjrjuBoCkHVXgHSFTxLwHAAFohDEBYThw6kUTUTUSgIANYhxgEhJheAYT9knNX5npXN3YZMJ01syQTAdYsVaRtY4UvpNR8SZYnpDUw0SlhlFwESEA6ZWTS1QIE8HAHdVMJjcIwpvpFpiA0xDRqhR9xt6RMQ2SnZp5OTfQzhToF9ElLkfNDB6SOpwoUYHJlpITZTx0R5WTjh2TlSlxYJ2iDIjJvQvQLhzddT4YIpVpfp+J-oKJgsmSH1-RzSHAlTFsuSSRHAK4hwcFQVDAujhTMiGTbJ3SkZPSDTgtxJfTcpeUAzLTgzdIsQ-5wxyFtBChCsXT5oEzEZDTkYJSqIEp6AYd0y9gvAdBlMKQ2ggJHTXBdJTE6k9J8pxVfMhSbj4yEZeIjSUzayCiGyagmzDUKRWzUIGoLhYJjhn56lgdf1rgYyhy9TxS4oPT+o9zUZ04pzwksygyVSKowxYleyv8DB3QKRSyxSFpDzKzkzqy0YvSEgpyMRuUvZAzEkrSQyrhqZG0FxElzcKhHzOo3SKyYpPyepMAPy6yARvyl4DIcQvZ0RAyJ4KpOhdAfBnp6RgUChDjpCfdhyYLRyqyXykLJy78uC0KRFMKlICEcLqglxoJdBDJalOS3QoL9T9xIBvzzhgkTo-BDhOT6RlynBAxeKzAmymhW8NC4YyzuQ6KTSBwyphw-zsyLz2L0RpEhwsQLhjhjJ0jYyfcN4pzsErlTgWjTBp9PBJFqQ85tZyFaRCsugKcyK14YM2dw5vyCFnBSY-AZ1Zx7lOzlZh4GhgKlJPZHFiNt4yMKMpiKjlQpywxv4IVjU7cvAlIfAtzwCGB6tetADMApy1h8L5YtgKhvsCkDhK8Q5HB9hKcWdkdV4TycNH9X1Z90CRc9IXAQljLtYbUZc-d1pFd+Ypyn5xMXMP5STo9CYFklxTl8QU8irJpO9prOKPj1jvitifZPBSUATShFxCsQdui14tDoD+sr84CpzZwp1TUPY1ThdMU6grlzgF4OyTgwxCrlKICz8mCFDWClDRpvziTDVsRzh7yChDBiUVZVJgVZkCRxdaZnD3kAjXdcj2diBprCSJMSSv4MDVyzkLhccgIryji7jTi5jzjwgMq+rMVm0obTgF4grE91qAbARKAiBIBmRYAAAxbeVAdmI8bAftSguhCAKcr63QYZMNBW04cFMTUoe5O3TBLoQIQIIAA */
  id: 'multiCartMachine',
  context: ({ input }: { input: MultiCartMachineInput }) => ({
    cartSummary: {
      status: 'loading' as const,
    },
    cartItems: {
      status: 'loading' as const,
    },
    cartMembers: {
      status: 'loading' as const,
    },
    // Initialize with Redux values
    memberId: input.memberId,
    history: input.history,
    currentRegionCode: input.currentRegionCode,
    sharedCartsFeatureFlag: input.sharedCartsFeatureFlag,
    creatingNewCart: false,
  }),
  // Determine initial state - no always transitions at the top level to avoid recursion
  initial: 'checkingState',
  states: {
    checkingState: {
      always: [
        {
          target: 'initializing',
          guard: 'isUserLoggedIn'
        },
        {
          target: 'ready'
        }
      ]
    },
    initializing: {
      initial: 'loadingCartSummary',
      states: {
        loadingCartSummary: {
          invoke: {
            src: 'getMultiCarts',
            input: ({ context }: { context: MultiCartMachineContext }) => ({
              isSharedCartsFeatureEnabled: context.sharedCartsFeatureFlag,
              isSharedCartInvitePage: context.history.location.pathname.includes('/cart/s/')
            }),
            onDone: [{
              guard: or(['hasSharedCart', 'isSharedCartsFeatureEnabled', 'isSharedCartInvitePage']),
              target: 'loadingCartDetails',
              actions: ['assignCartSummary', 'loadActiveCartId']
            }, {
              guard: not('isSharedCartsFeatureEnabled'),
              target: '#sharedCartsFeatureDisabled',
              actions: ['assignCartSummary', 'loadActiveCartId']
            }, {
              target: 'loadingCartDetails',
              actions: ['assignCartSummary', 'loadActiveCartId']
            }],
            onError: {
              target: 'error',
              actions: ['logErrorEvent', 'assignCartSummaryFetchError']
            },
          }
        },
        loadingCartDetails: {
          type: 'parallel',
          onDone: {
            target: 'loaded'
          },
          states: {
            loadingCartItems: {
              initial: 'loading',
              states: {
                loading: {
                  invoke: {
                    src: 'getMultiCartItems',
                    input: ({ context }) => context.cartSummary.status === 'loaded' && context.cartSummary.data ? Object.keys(context.cartSummary.data) : [],
                    onDone: {
                      target: 'done',
                      actions: 'assignCartItems'
                    },
                    onError: {
                      target: 'error',
                      actions: 'assignCartItemsFetchError'
                    },
                  }
                },
                done: {
                  type: 'final',
                },
                error: {
                  type: 'final',
                },
              }
            },
            loadingCartUsers: {
              initial: 'loading',
              states: {
                loading: {
                  invoke: {
                    src: 'getMultiCartMembers',
                    input: ({ context }) => context.cartSummary.status === 'loaded' && context.cartSummary.data ? Object.keys(context.cartSummary.data) : [],
                    onDone: {
                      target: 'done',
                      actions: 'assignCartMembers'
                    },
                    onError: {
                      target: 'error',
                      actions: 'assignCartMembersFetchError'
                    },
                  }
                },
                done: {
                  type: 'final',
                },
                error: {
                  type: 'final',
                },
              }
            },
          }
        },
        loaded: {
          type: 'final'
        },
        error: {
          type: 'final',
        },
      },
      onDone: {
        target: 'ready',
      }
    },
    ready: {
      on: {
        'successful login': {
          target: 'checkingState',
          actions: 'assignLoggedInMemberId'
        },
        'failed login': {
          target: 'ready',
        },
        'invite multi cart users': {
          target: 'inviting',
        },
        'create and move items to target cart': {
          target: 'creatingAndMovingToMultiCart'
        },
        'add items to active cart': {
          target: 'addingItemsToActiveCart',
        },
        'select and move items to target cart': {
          target: 'movingItemsToTargetCart',
          actions: [log(({ event }) => `** movingItemsToTargetCart ${JSON.stringify(event)}`)],
        },
        'remove active cart id': {
          target: 'ready',
          actions: ['removeActiveCart'],
        },
        'update multi cart': {
          target: 'ready',
          actions: 'assignUpdatedCartSummary',
        },
        'leave multi cart': {
          target: 'leavingMultiCart',
        },
        'revoke user access': {
          target: 'revokingUserAccess',
        },
        'set active cart': {
          target: 'ready',
          actions: [{
            type: 'assignActiveCartId',
            params: ({ event }: { event: SetActiveCart }) => ({ cartId: event.cartId })
          }],
        },
        'join multi cart': {
          target: 'joiningUserToMultiCart',
        },
        'update cart item': {
          target: 'ready',
          actions: 'updateCartItem',
        },
        'remove item from cart': {
          target: 'ready',
          actions: [
            'trackMultiCartDeleteItemEvent',
            { type: 'removeItemFromCart', params: ({ event }: { event: RemoveItemFromCart }) => ({ cartId: event.cartId, itemIds: event.itemIds }) }
          ],
        },
        'mark item as deleted': {
          target: 'ready',
          actions: 'markItemAsDeleted',
        },
        'restore item states': {
          target: 'ready',
          actions: 'restoreItemStates',
        },
        'empty cart items of active cart': {
          target: 'ready',
          actions: ['emptyCartItemsOfActiveCart', 'showDeleteAllItemsFromCartSuccessSnackbar'],
        },
        'update cart items save for later': {
          target: 'ready',
          actions: ['sendSaveLaterTrackingEvent', 'updateCartItemsSaveForLater'],
        },
        'purchase active cart items': {
          target: 'purchasingActiveCartItems',
        },
        'delete items from given cart': {
          target: 'ready',
          actions: ['spawnDeleteItemMachine', 'sendSoftDeleteEvent']
        },
        'hard delete items from given cart': {
          target: 'ready',
          actions: ['spawnDeleteItemMachine', 'sendHardDeleteEvent'],
        },
        'undo delete': {
          target: 'ready',
          actions: ['sendUndoDeleteItemEvent'],
        },
      },
    },
    creatingAndMovingToMultiCart: {
      invoke: {
        src: 'createAndMoveItemsToTargetCart',
        input: ({ event, context }) => {
          assertEvent(event, 'create and move items to target cart')

          return {
            sourceCartId: context.activeCartId!,
            cartName: event.cartName,
            itemIdsToMove: event.multiCartItemIdsToMove,
            skipNotification: event.skipNotification,
          }
        },
        onDone: [{
          target: 'fetchingActiveCartItems',
          actions: [
            // Remove items from source cart
            { type: 'removeItemFromCart', params: ({ event }: { event: DoneActorEvent<{ cartId: string, itemIds: Array<string> }, string> }) => ({ cartId: event.output.cartId, itemIds: event.output.itemIds }) },
            { type: 'assignActiveCartId', params: ({ event }: { event: DoneActorEvent<{ createdCartId: App.MultiCartSummary, targetCartId: string }, string> }) => ({ cartId: event.output.targetCartId }) },
            'assignNewlyCreatedCartSummary',
            'multiCartCreateCartEvent'
          ],
          guard: ({ event }) => {
            assertEvent(event, ['xstate.done.actor.0.multiCartMachine.creatingAndMovingToMultiCart'])
            return event.output.skipNotification
          }
        },
        {
          target: 'fetchingActiveCartItems',
          actions: [
            'showCreateCartSuccessSnackbar',
            // Remove items from source cart
            { type: 'removeItemFromCart', params: ({ event }: { event: DoneActorEvent<{ cartId: string, itemIds: Array<string> }, string> }) => ({ cartId: event.output.cartId, itemIds: event.output.itemIds }) },
            { type: 'assignActiveCartId', params: ({ event }: { event: DoneActorEvent<{ createdCartId: App.MultiCartSummary, targetCartId: string }, string> }) => ({ cartId: event.output.targetCartId }) },
            'assignNewlyCreatedCartSummary'
          ]
        }],
        onError: {
          target: 'ready',
          actions: 'showCreateCartErrorSnackbar'
        },
      },
    },
    addingItemsToActiveCart: {
      invoke: {
        src: 'addItemsToMultiCart',
        input: ({ context, event }) => {
          assertEvent(event, ['add items to active cart'])

          return {
            cartId: context.activeCartId!,
            items: event.items,
            requestConnectedRooms: event.requestConnectedRooms,
            skipNotification: event.skipNotification,
          }
        },
        onDone: [{
          target: 'ready',
          actions: ['appendItemsToCart'],
          guard: ({ event }) => {
            assertEvent(event, ['xstate.done.actor.0.multiCartMachine.addingItemsToActiveCart'])
            return event.output.skipNotification
          }
        },
        {
          target: 'ready',
          actions: ['showAddItemsToCartSuccessSnackbar', 'appendItemsToCart']
        }],
        onError: {
          target: 'ready',
          actions: 'showAddItemsToCartErrorSnackbar'
        },
      },
    },
    leavingMultiCart: {
      invoke: {
        src: 'leaveMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'leave multi cart')
          return {
            cartId: event.cartId,
          }
        },
        onDone: {
          target: 'ready',
          actions: ['removeActiveCart', 'showLeaveCartSuccessSnackbar']
        },
        onError: {
          target: 'ready',
          actions: 'showLeaveCartErrorSnackbar'
        },
      },
    },
    revokingUserAccess: {
      invoke: {
        src: 'revokeUserAccessMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'revoke user access')
          return {
            cartId: event.cartId,
            userId: event.userId,
          }
        },
        onDone: {
          target: 'ready',
          actions: ['showRevokeUserAccessSuccessSnackbar', 'assignRevokedUserAccess']
        },
        onError: {
          target: 'ready',
          actions: 'showRevokeUserAccessErrorSnackbar'
        },
      },
    },
    inviting: {
      invoke: {
        src: 'inviteUsersToMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'invite multi cart users')
          return {
            cartId: event.cartId,
            users: event.users,
          }
        },
        onDone: {
          target: 'fetchingNewCartMembers',
          actions: 'showInvitationSuccessSnackbar'
        },
        onError: {
          target: 'ready',
          actions: 'showInvitationErrorSnackbar'
        },
      },
    },
    fetchingActiveCartItems: {
      invoke: {
        src: 'getMultiCartItems',
        input: ({ context }) => [context.activeCartId!],
        onDone: {
          target: 'fetchingNewCartMembers',
          actions: 'appendCartItems'
        },
        onError: {
          target: 'ready',
          actions: 'assignCartItemsFetchError',
        },
      },
    },
    fetchingNewCartMembers: {
      invoke: {
        src: 'getMultiCartMembers',
        input: ({ context }) => [context.activeCartId!],
        onDone: {
          target: 'ready',
          actions: 'appendCartMembers'
        },
        onError: {
          target: 'ready',
          actions: 'assignCartMembersFetchError',
        },
      },
    },
    joiningUserToMultiCart: {
      invoke: {
        src: 'joinUserToMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'join multi cart')
          return {
            sharedCartId: event.sharedCartId,
          }
        },
        onDone: {
          target: 'initializing',
          actions: ['showJoinCartSuccessSnackbar', 'pushToCartPage',
            {
              type: 'assignActiveCartId',
              params: ({ event }: { event: DoneActorEvent<{ newlyJoinedCartId: string }, string> }) => ({ cartId: event.output.newlyJoinedCartId })
            }]
        },
        onError: {
          target: 'ready',
          actions: ['showJoinCartErrorSnackbar', 'pushToCartPage'],
        },
      },
    },
    movingItemsToTargetCart: {
      invoke: {
        src: 'moveItemsToTargetCart',
        input: ({ event, context }) => {
          assertEvent(event, 'select and move items to target cart')

          return {
            sourceCartId: context.activeCartId!,
            targetCartId: event.targetCartId,
            itemIdsToMove: event.multiCartItemIdsToMove,
          }
        },
        onDone: {
          target: 'fetchingActiveCartItems',
          actions: [{
            type: 'assignActiveCartId',
            params: ({ event }: { event: DoneActorEvent<{ targetCartId: string }, string> }) => ({ cartId: event.output.targetCartId })
          },
          // Remove items from source cart
          { type: 'removeItemFromCart', params: ({ event }: { event: DoneActorEvent<{ cartId: string, itemIds: Array<string> }, string> }) => ({ cartId: event.output.cartId, itemIds: event.output.itemIds }) },
          'showMoveItemsToCartSuccessSnackbar'],
        },
        onError: {
          target: 'ready',
          actions: 'showAddItemsToCartErrorSnackbar'
        },
      },
    },
    purchasingActiveCartItems: {
      invoke: {
        src: 'purchaseMultiCartItems',
        input: ({ event, context }) => {
          assertEvent(event, 'purchase active cart items')
          return {
            multiCartId: context.activeCartId!,
            checkoutCartId: event.checkoutCartId,
            orderId: event.orderId,
          }
        },
        onDone: {
          target: 'initializing',
        },
        onError: {
          target: 'ready',
        },
      },
    },
    sharedCartsFeatureDisabled: {
      id: 'sharedCartsFeatureDisabled',
      type: 'final',
    }
  },
})

export default multiCartMachine
